import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABEL, hr, statusBadgeVariant, formatEmployeeName } from "../lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { CalendarDays, HomeIcon, FileDown, UserCheck, XCircle, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function HistoryItem({ r, kind }) {
  // kind: 'vacation' | 'wfh'
  const pdfUrl = kind === "vacation"
    ? `${BACKEND}/api/vacation/requests/${r.id}/pdf`
    : `${BACKEND}/api/wfh/requests/${r.id}/pdf`;
  const detailLink = kind === "vacation" ? `/zahtjev/${r.id}` : `/wfh/zahtjev/${r.id}`;
  const headline = kind === "vacation"
    ? `${hr(r.collective_start) || "—"} – ${hr(r.collective_end) || "—"}`
    : `${hr(r.start_date)} – ${hr(r.end_date)}`;

  const isRejected = r.status === "rejected";
  const isApproved = ["approved", "decided", "sent"].includes(r.status);
  const hasPdf = ["decided", "sent"].includes(r.status);

  return (
    <div className="block p-3 rounded border hover:border-primary/60 transition-colors space-y-1.5"
         data-testid={`history-item-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Link to={detailLink} className="text-sm font-medium hover:underline">{headline}</Link>
        <Badge variant={statusBadgeVariant(r.status)} data-testid={`history-status-${r.id}`}>
          {STATUS_LABEL[r.status]}
        </Badge>
      </div>
      <div className="text-xs text-muted-foreground">
        {kind === "vacation"
          ? <>Ukupno <b>{r.total_days}</b> radnih dana · predano {hr(r.created_at)}</>
          : <>{r.broj_mobitela ? `Mob. ${r.broj_mobitela} · ` : (r.lokacija ? `${r.lokacija} · ` : "")}predano {hr(r.created_at)}</>}
      </div>
      {kind === "vacation" && r.additional_periods?.length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          Dodatni periodi: {r.additional_periods.map(p => `${hr(p.start)}–${hr(p.end)}`).join("; ")}
        </div>
      )}
      {isApproved && r.approver_name && (
        <div className="text-[11px] flex items-center gap-1 text-green-700">
          <UserCheck className="w-3 h-3"/> Suglasnost dao/dala: <b>{r.approver_name}</b>
          {r.manager_decided_at && <span className="text-muted-foreground">· {hr(r.manager_decided_at)}</span>}
        </div>
      )}
      {isRejected && (
        <div className="text-[11px] flex items-start gap-1 text-red-700">
          <XCircle className="w-3 h-3 mt-0.5 shrink-0"/>
          <span>
            Odbijeno{r.approver_name ? ` (${r.approver_name})` : ""}
            {r.manager_comment ? <>: <i>{r.manager_comment}</i></> : null}
          </span>
        </div>
      )}
      {(r.klasa || r.urbroj) && (
        <div className="text-[11px] text-muted-foreground">
          {r.klasa && <>KLASA <code>{r.klasa}</code></>}
          {r.urbroj && <> · URBROJ <code>{r.urbroj}</code></>}
        </div>
      )}
      {hasPdf && (
        <a href={pdfUrl} target="_blank" rel="noreferrer"
           data-testid={`history-pdf-${r.id}`}>
          <Button variant="outline" size="sm" className="h-7 text-xs">
            <FileDown className="w-3 h-3 mr-1"/> Rješenje (PDF)
          </Button>
        </a>
      )}
    </div>
  );
}

export default function EmployeeHistorySheet({ employee, onClose }) {
  const open = !!employee;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!employee) { setData(null); setError(""); return; }
    setLoading(true); setError("");
    api.get(`/users/${encodeURIComponent(employee.email)}/history`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Greška pri učitavanju povijesti"))
      .finally(() => setLoading(false));
  }, [employee]);

  const vacation = data?.vacation || [];
  const wfh = data?.wfh || [];
  const summary = data?.summary || {};

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto" data-testid="employee-history-sheet">
        <SheetHeader>
          <SheetTitle className="serif text-xl" data-testid="history-employee-name">
            {employee && formatEmployeeName(employee)}
          </SheetTitle>
          <SheetDescription>
            {employee?.email} · {employee?.radno_mjesto || employee?.katedra}
          </SheetDescription>
        </SheetHeader>

        {loading && <p className="text-xs text-muted-foreground mt-6">Učitavanje povijesti…</p>}
        {error && <p className="text-sm text-red-600 mt-6" data-testid="history-error">{error}</p>}

        {!loading && !error && employee && (
          <>
            <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="history-export-actions">
              <a href={`${BACKEND}/api/users/${encodeURIComponent(employee.email)}/history.pdf`}
                 target="_blank" rel="noreferrer"
                 data-testid="history-export-pdf">
                <Button variant="outline" size="sm" className="h-8">
                  <FileText className="w-3.5 h-3.5 mr-1.5"/> Izvezi PDF
                </Button>
              </a>
              <a href={`${BACKEND}/api/users/${encodeURIComponent(employee.email)}/history.xlsx`}
                 data-testid="history-export-xlsx">
                <Button variant="outline" size="sm" className="h-8">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/> Izvezi Excel
                </Button>
              </a>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-secondary/40 rounded p-3" data-testid="history-summary-vacation">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">GO 2026</div>
                <div className="serif text-2xl font-semibold mt-1">{summary.vacation_used_days ?? 0}</div>
                <div className="text-xs text-muted-foreground">od {data?.employee?.ukupni_fond_dana ?? 30} dana fonda</div>
              </div>
              <div className="bg-secondary/40 rounded p-3" data-testid="history-summary-wfh">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Rad od kuće</div>
                <div className="serif text-2xl font-semibold mt-1">{summary.wfh_approved_count ?? 0}</div>
                <div className="text-xs text-muted-foreground">odobrenih zahtjeva</div>
              </div>
            </div>

            <h3 className="text-sm font-medium mt-6 mb-2 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary"/>
              Godišnji odmor ({vacation.length})
            </h3>
            <div className="space-y-2">
              {vacation.length === 0 && <p className="text-xs text-muted-foreground">Nema predanih zahtjeva.</p>}
              {vacation.map(r => <HistoryItem key={r.id} r={r} kind="vacation"/>)}
            </div>

            <h3 className="text-sm font-medium mt-6 mb-2 flex items-center gap-2">
              <HomeIcon className="w-4 h-4 text-accent"/>
              Rad od kuće ({wfh.length})
            </h3>
            <div className="space-y-2 pb-8">
              {wfh.length === 0 && <p className="text-xs text-muted-foreground">Nema predanih zahtjeva.</p>}
              {wfh.map(r => <HistoryItem key={r.id} r={r} kind="wfh"/>)}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
